import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type Stat = { value: string; label: { en: string; bn: string } };

const STATS: Stat[] = [
  { value: "1,200+", label: { en: "Doctors on DoctorsX", bn: "ডাক্তার" } },
  { value: "180K+", label: { en: "Patients managed", bn: "রোগী পরিচালিত" } },
  { value: "৳ 4.2Cr", label: { en: "Billed via bKash & card", bn: "বিল সংগ্রহ" } },
  { value: "88%", label: { en: "Fewer no-shows", bn: "কম মিসড অ্যাপয়েন্টমেন্ট" } },
];

export default function TrustStatBanner() {
  const { lang } = useLang();
  const isBn = lang === "bn";
  const displayFont = isBn
    ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
    : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
      <div className="rounded-[32px] bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0c2340] px-6 sm:px-10 py-12 sm:py-14 text-white relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />

        <div className="relative grid gap-10 lg:grid-cols-[1.1fr_1fr] items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#7dd3fc] px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
              <Star className="w-3 h-3 fill-current" />
              {isBn ? "বিশ্বাস অর্জিত" : "Voted #1"}
            </div>
            <h2
              className="mt-5 text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.1] tracking-tight"
              style={{ fontFamily: displayFont }}
            >
              {isBn ? (
                <>বাংলাদেশের ১,২০০+ ডাক্তার<br />DoctorsX-এ ভরসা রেখেছেন</>
              ) : (
                <>Over 1,200 doctors across Bangladesh<br />trust DoctorsX every day<span className="text-[#7dd3fc]">*</span></>
              )}
            </h2>
            <p className="mt-4 text-[15px] text-white/70 max-w-md leading-relaxed">
              {isBn
                ? "চেম্বার, প্রেসক্রিপশন, বিলিং, রিমাইন্ডার — সবকিছু এক জায়গায়, বাংলায়।"
                : "From prescriptions to bKash billing to WhatsApp reminders — one platform, built for Bangladeshi practice."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/stories"
                className="inline-flex items-center gap-2 bg-white text-[#0f172a] hover:bg-[#f1f5f9] text-[14px] font-semibold px-5 py-3 rounded-full transition-colors"
              >
                {isBn ? "ডাক্তারদের গল্প" : "See doctor stories"}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 border border-white/25 hover:bg-white/10 text-white text-[14px] font-semibold px-5 py-3 rounded-full transition-colors"
              >
                {isBn ? "১৪ দিন ফ্রি ট্রায়াল" : "Start 14-day trial"}
              </Link>
            </div>
            <p className="mt-4 text-[11px] text-white/50">
              {isBn
                ? "*২০২৬ সালের অক্টোবরের অভ্যন্তরীণ পরিসংখ্যান অনুযায়ী।"
                : "*Based on DoctorsX system data, October 2026."}
            </p>
          </div>

          {/* Stat grid */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            {STATS.map((s) => (
              <div
                key={s.value}
                className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-5 sm:p-6 hover:bg-white/10 transition-colors"
              >
                <div
                  className="text-3xl sm:text-4xl font-bold text-white tracking-tight"
                  style={{ fontFamily: displayFont }}
                >
                  {s.value}
                </div>
                <div className="mt-1.5 text-[12px] text-white/70 leading-tight">
                  {isBn ? s.label.bn : s.label.en}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
